import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { AreaChart, Area, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { fmtPrice } from "../lib/format";
import { API } from "../lib/constants";

const HistoryTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border-[3px] border-ocean-navy bg-white px-3 py-1.5 font-display text-sm font-semibold">
      {fmtPrice(payload[0].value)}
    </div>
  );
};

export default function Sparkline() {
  const { data, isLoading } = useQuery({
    queryKey: ["blue-history"],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/price-history`);
      return data;
    },
    refetchInterval: 60000,
    staleTime: 60000,
  });

  const hist = data?.history || [];
  const up = hist.length >= 2 && hist[hist.length - 1].c >= hist[0].c;
  const stroke = up ? "#16A34A" : "#E11D48";
  const fill = up ? "#4ADE80" : "#FF70A6";

  return (
    <div
      data-testid="sparkline-panel"
      className="sticker-sm relative h-64 rounded-2xl bg-white p-4 sm:h-72"
    >
      {isLoading || !data ? (
        <div className="flex h-full flex-col items-center justify-center gap-3">
          <span className="h-9 w-9 animate-spin rounded-full border-4 border-ocean-sky border-t-transparent" />
          <p className="text-sm font-bold text-ocean-muted">Diving for candles…</p>
        </div>
      ) : !data.ok ? (
        <div className="flex h-full items-center justify-center">
          <p className="max-w-xs text-center text-sm font-bold text-ocean-muted">
            Not enough waves yet — the candle chart surfaces as the pool grows.
          </p>
        </div>
      ) : (
        <>
          <p className="absolute left-5 top-4 text-[11px] font-extrabold uppercase tracking-[0.18em] text-ocean-muted">
            Last 48h · hourly
          </p>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={hist} margin={{ top: 34, right: 6, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="waveFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={fill} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={fill} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <YAxis domain={["auto", "auto"]} hide />
              <Tooltip content={<HistoryTooltip />} />
              <Area
                dataKey="c"
                type="monotone"
                stroke={stroke}
                strokeWidth={3}
                fill="url(#waveFill)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
